import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { SERVICE_ICONS, serviceIcon } from "@/components/serviceIcons";
import { ArrowUp, ArrowDown, Plus, Pencil, Trash2 } from "lucide-react";

const TYPES = ["text", "number", "date", "month"];
const keyOf = (label, i) => (label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "field") .replace(/^(\d)/, "f_$1").slice(0, 28) + (i ? `_${i}` : "");

function FieldsEditor({ fields, onChange }) {
  const set = (i, patch) => onChange(fields.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  return (
    <div className="space-y-2" data-testid="service-fields-editor">
      {fields.map((f, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input value={f.label} placeholder="Label" onChange={(e) => set(i, { label: e.target.value })}
                 className="flex-1" data-testid={`service-field-label-${i}`} />
          <Select value={f.type} onValueChange={(v) => set(i, { type: v })}>
            <SelectTrigger className="w-28" data-testid={`service-field-type-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <label className="flex items-center gap-1 text-xs whitespace-nowrap">
            <Switch checked={f.required} onCheckedChange={(v) => set(i, { required: v })} data-testid={`service-field-req-${i}`} /> Req.
          </label>
          <Button type="button" size="sm" variant="ghost" onClick={() => onChange(fields.filter((_, j) => j !== i))}
                  aria-label="Remove field" data-testid={`service-field-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" className="rounded-full" data-testid="service-add-field"
              onClick={() => onChange([...fields, { key: "", label: "", type: "text", required: true }])}>
        <Plus className="h-3.5 w-3.5 mr-1" /> Add field
      </Button>
      <p className="text-xs" style={{ color: "var(--text-muted)" }}>Every service also gets an optional note (200 characters).</p>
    </div>
  );
}

function ServiceDialog({ service, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  useEffect(() => { setForm(service ? { name: "", icon: "globe", active: true, fields: [], ...service } : null); }, [service]);
  if (!form) return null;
  const save = async () => {
    const used = new Set();
    const fields = form.fields.filter((f) => f.label.trim()).map((f) => {
      let k = f.key || keyOf(f.label, 0), n = 1;
      while (used.has(k)) k = keyOf(f.label, n++);
      used.add(k);
      return { key: k, label: f.label.trim(), type: f.type, required: f.required, min_field: f.min_field || null };
    });
    const body = { name: form.name, icon: form.icon, active: form.active, fields };
    try {
      if (form.id) await api.patch(`/services/${form.id}`, body);
      else await api.post("/services", body);
      toast.success("Service saved");
      onSaved();
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="service-dialog">
        <DialogHeader><DialogTitle>{form.id ? "Edit service" : "New service"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-[1fr_140px] gap-3">
            <div className="space-y-1"><Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="service-name-input" /></div>
            <div className="space-y-1"><Label>Icon</Label>
              <Select value={form.icon} onValueChange={(v) => setForm({ ...form, icon: v })}>
                <SelectTrigger data-testid="service-icon-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(SERVICE_ICONS).map(([k, I]) => (
                    <SelectItem key={k} value={k}><span className="inline-flex items-center gap-2"><I className="h-4 w-4" />{k}</span></SelectItem>
                  ))}
                </SelectContent>
              </Select></div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="service-active-input" /> Active
          </label>
          <Label>Form fields</Label>
          <FieldsEditor fields={form.fields} onChange={(fields) => setForm({ ...form, fields })} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={form.name.trim().length < 2} className="bg-navy text-white" data-testid="service-save-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ServicesPanel({ readOnly = false }) {
  const { user } = useAuth();
  const canEdit = !readOnly && user?.role === "admin";
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/services").then(({ data }) => setList(data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); }, []);

  const move = async (i, d) => {
    const next = [...list];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    setList(next);
    try { await api.post("/services/reorder", { ids: next.map((s) => s.id) }); } catch (e) { toast.error(formatError(e)); load(); }
  };
  const toggle = async (s) => {
    try { await api.patch(`/services/${s.id}`, { active: !s.active }); load(); } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <section data-testid={readOnly ? "services-readonly" : "services-panel"}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xl font-bold">Services {readOnly && <span className="text-xs font-normal" style={{ color: "var(--text-muted)" }}>(view only)</span>}</h2>
        {canEdit && <Button onClick={() => setEditing({})} className="rounded-xl bg-navy text-white" data-testid="add-service-btn">
          <Plus className="h-4 w-4 mr-1" /> Add service</Button>}
      </div>
      <div className="w-card-flat overflow-x-auto">
        <table className="w-full text-sm">
          <thead style={{ background: "var(--bg-soft)" }}>
            <tr>{["#", "Service", "Fields", "Active", ""].map((h) => <th key={h} className="text-left px-4 py-3 label-caps">{h}</th>)}</tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={5} className="p-6 text-center" style={{ color: "var(--text-muted)" }}>No services available.</td></tr>}
            {list.map((s, i) => {
              const Icon = serviceIcon(s.icon);
              return (
                <tr key={s.id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`service-row-${s.id}`}>
                  <td className="px-4 py-3">{i + 1}</td>
                  <td className="px-4 py-3 font-medium"><span className="inline-flex items-center gap-2"><Icon className="h-4 w-4 gold" />{s.name}</span></td>
                  <td className="px-4 py-3 text-xs" style={{ color: "var(--text-muted)" }}>{s.fields.map((f) => f.label + (f.required ? "" : " (opt)")).join(", ")}</td>
                  <td className="px-4 py-3">
                    {canEdit ? <Switch checked={s.active} onCheckedChange={() => toggle(s)} data-testid={`service-toggle-${s.id}`} />
                      : <span className="text-xs">{s.active ? "Active" : "Inactive"}</span>}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {canEdit && <>
                      <Button size="sm" variant="ghost" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up" data-testid={`service-up-${s.id}`}><ArrowUp className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" disabled={i === list.length - 1} onClick={() => move(i, 1)} aria-label="Move down" data-testid={`service-down-${s.id}`}><ArrowDown className="h-4 w-4" /></Button>
                      <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => setEditing(s)} data-testid={`service-edit-${s.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
                    </>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {canEdit && <ServiceDialog service={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </section>
  );
}
